'use client';

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useDeviceTypeContext } from '@/app/utils/getDeviceType';
import { useRouter, useSearchParams } from 'next/navigation';
import { IconSearch, IconX, IconLayoutGrid, IconList } from '@tabler/icons-react';

import { client } from '@/app/utils/client';
// import { fetchContributors as fetchContributorsUtil } from '@/app/utils/fetchContributors';
import { getRelatedBlogs } from '@/app/utils/relatedContentHandler';
import { parseCommaSeparatedValues } from '@/app/utils/stringUtils';
import ListingContentlets from '../shared/listingContentlets';
import LoadingPosts from '@/components/shared/LoadingPosts';
import NoResults from '@/components/shared/NoResults';
import { CollectionCard } from './collection-type/components/collectionCard';
import { CallToActionButton } from '@/components/shared/CallToActionButton';
import CategoryButton from '../shared/categoryButton';
import { normalizeTags } from '@/app/utils/tags';
import FilterLabel from '@/components/shared/FilterLabel';
import { LIMIT_500, DEFAULT_LANGUAGE_ID } from '@/app/utils/constants';

function parseWidgetTags(tags) {
    if (typeof tags === 'string') {
        return parseCommaSeparatedValues(tags)
            .map((tag) => ({
                key: tag,
                categoryName: normalizeTags(tag.charAt(0).toUpperCase() + tag.slice(1))
            }))
            .sort((a, b) => a.categoryName.localeCompare(b.categoryName));
    }

    return Array.isArray(tags)
        ? [...tags].sort((a, b) => (a.categoryName || '').localeCompare(b.categoryName || ''))
        : [];
}

function mapContributorsToOptions(contributorIds, contributors) {
    return contributorIds.map((contributorId) => {
        const contributor = contributors.find(
            (c) => c.identifier === contributorId || c.inode === contributorId
        );
        return {
            value: contributorId,
            label: contributor ? `${contributor.firstName} ${contributor.lastName}` : contributorId
        };
    });
}

function getEffectivePosts(
    isSearchByTags,
    showContributorFilter,
    activeTags,
    activeContributors,
    fetchedPosts,
    defaultPosts
) {
    if (
        (isSearchByTags && activeTags.length > 0) ||
        (showContributorFilter && activeContributors.length > 0)
    ) {
        return Array.isArray(fetchedPosts) ? fetchedPosts : [];
    }

    return defaultPosts;
}

const DEFAULT_LIMIT = 20;
const MIN_CATEGORY_ITEMS = 5;

function RelatedBlogs({ widgetCodeJSON, content, ...rest }) {
    const deviceType = useDeviceTypeContext();
    const [activeTags, setActiveTags] = useState([]);
    const [activeContributors, setActiveContributors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchedPosts, setFetchedPosts] = useState(null);
    const contributors =
        content.contributors?.sort((a, b) => a.firstName.localeCompare(b.firstName)) || [];
    const [visibleCount, setVisibleCount] = useState(DEFAULT_LIMIT);
    const [controlRelatedPosts, setControlRelatedPosts] = useState([]);
    const [controlLoading, setControlLoading] = useState(false);
    const [isShowInList, setIsShowInList] = useState(false);

    const router = useRouter();
    const componentRef = useRef(null);
    const searchParams = useSearchParams();
    const searchQueryFromUrl = searchParams.get('q') || '';
    const [searchInputValue, setSearchInputValue] = useState(searchQueryFromUrl);
    const [searchQuery, setSearchQuery] = useState(searchQueryFromUrl);

    // Sync input value when URL changes externally
    useEffect(() => {
        setSearchInputValue(searchQueryFromUrl);
        setSearchQuery(searchQueryFromUrl);
    }, [searchQueryFromUrl]);

    // Debounce URL update
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (searchInputValue !== searchQueryFromUrl) {
                const params = new URLSearchParams(searchParams.toString());
                if (searchInputValue) {
                    params.set('q', searchInputValue);
                } else {
                    params.delete('q');
                }
                router.replace(`?${params.toString()}`, { scroll: false });
            }
            setSearchQuery(searchInputValue);
        }, 1000);

        return () => clearTimeout(timeoutId);
    }, [searchInputValue, searchQueryFromUrl, searchParams, router]);

    const handleSearchChange = useCallback((value) => {
        setSearchInputValue(value);
    }, []);

    const handleClearSearch = useCallback(() => {
        setSearchInputValue('');
        setSearchQuery('');
        const params = new URLSearchParams(searchParams.toString());
        params.delete('q');
        router.replace(`?${params.toString()}`, { scroll: false });
    }, [searchParams, router]);

    const { posts = [] } = widgetCodeJSON;

    const {
        widgetTitle,
        category,
        isSearchByTags,
        showContributorFilter,
        // filterContributorByCompany,
        isShowWidgetTitle,
        showAuthor,
        showTopMargin,
        maxLimit = 5,
        tags,
        renderOnlyCategories,
        showCategory,
        button,
        relatedBlogsOffAutoGeneration,
        relatedBlogsManualShowPosts,
        centerBlogs,
        // contributorLimit,
        imagePriorityByDevice,
        ...restConfig
    } = rest;

    // Kept on `rest` on purpose: `identifier` is part of `restConfig`, which is forwarded
    // to getRelatedBlogs(), so it must not be destructured out of the component signature.
    const contentletIdentifier = rest.identifier;

    // Identify if this is the "All Posts" section
    const isAllPostsSection = isSearchByTags && !isShowWidgetTitle;

    useEffect(() => {
        if (activeTags.length > 0 || activeContributors.length > 0) {
            setVisibleCount(DEFAULT_LIMIT);
        }
    }, [activeTags, activeContributors]);

    // useEffect(() => {
    //     const run = async () => {
    //         if (!showContributorFilter) return;
    //         const list = await fetchContributorsUtil({
    //             companyId: filterContributorByCompany,
    //             contributorLimit
    //         });
    //         setContributors(list);
    //     };

    //     run();
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [showContributorFilter, filterContributorByCompany]);

    useEffect(() => {
        const fetchPosts = async () => {
            const shouldFetchByTags = isSearchByTags && activeTags.length > 0;
            const shouldFetchByContributors =
                showContributorFilter && activeContributors.length > 0;

            if (!shouldFetchByTags && !shouldFetchByContributors) {
                setFetchedPosts(null);
                return;
            }

            setLoading(true);
            try {
                const builder = client.content
                    .getCollection('Blog')
                    .query((qb) => {
                        let query = qb.field('showInListing').equals('true');
                        return query;
                    })
                    .sortBy([{ field: 'Blog.postingDate', order: 'desc' }])
                    .limit(LIMIT_500)
                    .language(DEFAULT_LANGUAGE_ID);

                const queries = [];

                if (shouldFetchByTags) {
                    const tagsQuery = activeTags.map((tag) => `Blog.tags:${tag}`).join(' ');
                    queries.push(`(${tagsQuery})`);
                }

                if (shouldFetchByContributors) {
                    const authorsQuery = activeContributors
                        .map((contributorId) => `Blog.author:${contributorId}`)
                        .join(' ');
                    queries.push(`(${authorsQuery})`);
                }

                if (queries.length > 0) {
                    const combinedQuery = queries.map((q) => `+${q}`).join(' ');
                    builder.query(combinedQuery);
                }

                const result = await builder;
                const contentlets = Array.isArray(result?.contentlets) ? result.contentlets : [];
                setFetchedPosts(contentlets);
            } catch (error) {
                console.error('Error fetching posts:', error);
                setFetchedPosts([]);
            } finally {
                setLoading(false);
            }
        };

        fetchPosts();
    }, [isSearchByTags, activeTags, showContributorFilter, activeContributors]);

    useEffect(() => {
        const fetchControlRelatedBlogs = async () => {
            if (!relatedBlogsOffAutoGeneration) {
                setControlLoading(false);
                return;
            }

            setControlLoading(true);
            try {
                const posts = await getRelatedBlogs({
                    ...restConfig,
                    relatedBlogsOffAutoGeneration,
                    relatedBlogsShowPosts: relatedBlogsManualShowPosts,
                    relatedBlogsLimit: maxLimit,
                    extractedCategories: null,
                    currentTitle: ''
                });
                setControlRelatedPosts(posts);
            } catch (error) {
                console.error('Error fetching control related blogs:', error);
                setControlRelatedPosts([]);
            } finally {
                setControlLoading(false);
            }
        };

        fetchControlRelatedBlogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Modify parent container margin from child
    useEffect(() => {
        const element = componentRef.current;
        if (!element) return;

        const parent = element.parentElement;
        if (!parent) return;

        // Contentlet identifiers this margin override applies to.
        const allowedIdentifiers = ['c372b4dd5eeda3bbd92f6e168a863f8f'];

        // Read the identifier from props rather than the parent's `data-dot-identifier`.
        // The SDK only emits that attribute in UVE edit mode, so on the published site the
        // check below always failed and the override silently stopped applying. The prop
        // carries the same value the attribute used to.
        const shouldApplyMargin =
            allowedIdentifiers.length === 0 ||
            (contentletIdentifier && allowedIdentifiers.includes(contentletIdentifier));

        if (!shouldApplyMargin) return;

        // Store original margin values for cleanup
        const originalMarginTop = parent.style.marginTop;
        const originalMarginBottom = parent.style.marginBottom;
        const originalMarginLeft = parent.style.marginLeft;
        const originalMarginRight = parent.style.marginRight;

        // Apply margin to parent (adjust these values as needed)
        // You can use Tailwind spacing values: 1rem = 16px, 2rem = 32px, etc.
        parent.style.marginTop = '0px';
        // parent.style.marginBottom = '2rem'; // Uncomment and adjust as needed
        // parent.style.marginLeft = '1rem'; // Uncomment and adjust as needed
        // parent.style.marginRight = '1rem'; // Uncomment and adjust as needed

        // Cleanup: restore original margins on unmount
        return () => {
            parent.style.marginTop = originalMarginTop;
            parent.style.marginBottom = originalMarginBottom;
            parent.style.marginLeft = originalMarginLeft;
            parent.style.marginRight = originalMarginRight;
        };
    }, [contentletIdentifier]);
    // Fetch all posts for "All Posts" section to enable pagination
    useEffect(() => {
        const fetchAllPosts = async () => {
            if (!isAllPostsSection || (showContributorFilter && activeContributors.length > 0))
                return;

            setLoading(true);
            try {
                const result = await client.content
                    .getCollection('Blog')
                    .query((qb) => qb.field('showInListing').equals('true'))
                    .sortBy([{ field: 'Blog.postingDate', order: 'desc' }])
                    .limit(LIMIT_500)
                    .language(DEFAULT_LANGUAGE_ID);

                const contentlets = Array.isArray(result?.contentlets) ? result.contentlets : [];
                setFetchedPosts(contentlets);
            } catch (error) {
                console.error('Error fetching all posts:', error);
                setFetchedPosts([]);
            } finally {
                setLoading(false);
            }
        };
        fetchAllPosts();
    }, [isAllPostsSection, activeContributors, showContributorFilter]);

    const buttonStyles =
        'h-[40px] sm:h-[44px] !border-0 !px-12 !py-[14px] rounded-lg text-base font-medium w-fit !bg-blue-100 !text-blue-700 hover:!bg-fuschia-200';

    const parsedTags = useMemo(() => parseWidgetTags(tags), [tags]);

    const goToCategoryPage = useCallback(() => {
        router.push(`/blog/${category[0].key}`);
    }, [router, category]);

    const rawEffectivePosts = useMemo(
        () =>
            getEffectivePosts(
                isSearchByTags,
                showContributorFilter,
                activeTags,
                activeContributors,
                fetchedPosts,
                posts
            ),
        [isSearchByTags, showContributorFilter, activeTags, activeContributors, fetchedPosts, posts]
    );

    const canLoadMore = useMemo(() => {
        // For "All Posts" section, enable Load More when there are more posts than visible
        if (isAllPostsSection) {
            return !loading && Array.isArray(fetchedPosts) && fetchedPosts.length > visibleCount;
        }

        // Original logic for filtered results
        return (
            (isSearchByTags || showContributorFilter) &&
            (activeTags.length > 0 || activeContributors.length > 0) &&
            !loading &&
            Array.isArray(rawEffectivePosts) &&
            rawEffectivePosts.length > visibleCount
        );
    }, [
        isAllPostsSection,
        isSearchByTags,
        showContributorFilter,
        activeTags.length,
        activeContributors.length,
        loading,
        fetchedPosts,
        rawEffectivePosts,
        visibleCount
    ]);

    const actualPosts = useMemo(() => {
        // For "All Posts" section, use fetched posts with pagination (no search filter)
        if (isAllPostsSection && Array.isArray(fetchedPosts) && fetchedPosts.length > 0) {
            return fetchedPosts.slice(0, visibleCount);
        }

        let postsToFilter = relatedBlogsOffAutoGeneration ? controlRelatedPosts : rawEffectivePosts;

        // Apply search filter if there's a query
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            postsToFilter = postsToFilter.filter((post) => {
                const title = (post.title || '').toLowerCase();
                return title.includes(query);
            });
        }

        if (relatedBlogsOffAutoGeneration) return postsToFilter;

        return postsToFilter.slice(
            0,
            (isSearchByTags || showContributorFilter) &&
                (activeTags.length > 0 || activeContributors.length > 0)
                ? visibleCount
                : MIN_CATEGORY_ITEMS
        );
    }, [
        isAllPostsSection,
        fetchedPosts,
        relatedBlogsOffAutoGeneration,
        controlRelatedPosts,
        rawEffectivePosts,
        isSearchByTags,
        showContributorFilter,
        activeTags,
        activeContributors,
        visibleCount,
        searchQuery
    ]);

    const getImagePriority = useCallback(
        (deviceType) => {
            if (!imagePriorityByDevice) return false;

            if (typeof imagePriorityByDevice === 'boolean') {
                return imagePriorityByDevice;
            }

            if (typeof imagePriorityByDevice === 'object') {
                const normalizedDeviceType =
                    deviceType === 'tablet-2-columns' || deviceType === 'tablet-3-columns'
                        ? 'tablet'
                        : deviceType;

                return (
                    imagePriorityByDevice[normalizedDeviceType] ||
                    imagePriorityByDevice[deviceType] ||
                    false
                );
            }

            return false;
        },
        [imagePriorityByDevice]
    );

    const CardComponent = useCallback(
        ({ contentlet, horizontalCard, showAuthor: showAuthorProp }) => {
            const priority = getImagePriority(deviceType);

            return (
                <CollectionCard
                    contentlet={contentlet}
                    contenttype={'Blog'}
                    showCategory={!!showCategory}
                    showAuthor={showAuthorProp !== undefined ? showAuthorProp : !!showAuthor}
                    horizontalCard={isSearchByTags ? isShowInList : horizontalCard}
                    priority={priority}
                    deviceType={deviceType}
                />
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [showCategory, showAuthor, deviceType, isSearchByTags, isShowInList]
    );

    const handleLoadMore = useCallback(() => {
        setVisibleCount((prev) => prev + DEFAULT_LIMIT);
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setActiveTags([]);
        setActiveContributors([]);
        setVisibleCount(DEFAULT_LIMIT);
    }, []);

    const handleFilterChange = useCallback((filterType, selectedItems) => {
        setVisibleCount(DEFAULT_LIMIT);

        if (filterType === 'tags') {
            setActiveTags(selectedItems.map((item) => item.value));
        }

        if (filterType === 'contributors') {
            setActiveContributors(selectedItems.map((item) => item.value));
        }
    }, []);

    if (renderOnlyCategories && Array.isArray(category)) {
        const categoryKeys = category.map((c) => Object.values(c)?.[4]).slice(0, maxLimit);
        const categoryLabels = category.map((c) => Object.values(c)?.[2]).slice(0, maxLimit);
        return (
            <div data-component="related-blogs">
                <div className="flex flex-wrap justify-center gap-3">
                    {button &&
                        button.length > 0 &&
                        button.map((cta, i) =>
                            cta.url ? (
                                <div key={cta.identifier + i}>
                                    <CallToActionButton
                                        buttonType="link"
                                        linkBtnAsStandartLink
                                        className={buttonStyles}
                                        {...cta}>
                                        {cta.urlName}
                                    </CallToActionButton>
                                </div>
                            ) : null
                        )}
                    {categoryKeys.map((key, index) => (
                        <a
                            aria-label={`Jump to ${categoryLabels[index]} section`}
                            key={key}
                            href={`#${key}`}>
                            <CategoryButton className="min-w-[197px] !py-2 text-base hover:!bg-fuschia-200 hover:text-black md:w-56">
                                {categoryLabels[index]}
                            </CategoryButton>
                        </a>
                    ))}
                </div>
                <div className="mx-auto mt-6 max-w-md">
                    <div className="relative">
                        <IconSearch className="absolute left-3 top-1/2 size-5 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchInputValue}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 bg-white px-10 py-3 text-gray-700 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                        {searchInputValue && (
                            <button
                                type="button"
                                onClick={handleClearSearch}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                aria-label="Clear search">
                                <IconX className="size-5" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            data-component="related-blogs"
            className="container"
            id={Object.values(category?.[0] || {})[4]}>
            {(rawEffectivePosts || (relatedBlogsOffAutoGeneration && !controlLoading)) && (
                <>
                    <div className="flex">
                        {isShowWidgetTitle && <h4 className="mr-6 mt-1 text-2xl">{widgetTitle}</h4>}
                        {button &&
                            button.length > 0 &&
                            button.map((cta, i) =>
                                cta.url ? (
                                    <div key={cta.identifier + i}>
                                        <CallToActionButton className={buttonStyles} {...cta}>
                                            {cta.urlName}
                                        </CallToActionButton>
                                    </div>
                                ) : null
                            )}
                        {isShowWidgetTitle && category && (
                            <CategoryButton
                                className="!py-2 px-10 text-base hover:!bg-fuschia-200 hover:text-black"
                                onClick={goToCategoryPage}>
                                Browse All
                            </CategoryButton>
                        )}

                        {isSearchByTags && !isShowWidgetTitle && (
                            <h4 className="mr-6 mt-1 text-2xl">All posts</h4>
                        )}
                    </div>

                    {isSearchByTags && (
                        <div className="!mt-8 flex flex-col gap-4 md:flex-row" id="posts">
                            <div className="relative z-10 flex flex-col">
                                <FilterLabel>Topics</FilterLabel>
                                <CategoryButton
                                    multiselect={true}
                                    enableSmartSearch={true}
                                    searchPlaceholder="Search topics..."
                                    options={parsedTags.map((tag) => ({
                                        value: tag.key,
                                        label: tag.categoryName
                                    }))}
                                    selectedOptions={activeTags.map((tag) => ({
                                        value: tag,
                                        label:
                                            parsedTags.find((t) => t.key === tag)?.categoryName ||
                                            tag
                                    }))}
                                    onSelectionChange={(selectedTags) =>
                                        handleFilterChange('tags', selectedTags)
                                    }
                                    defaultText="All topics"
                                    maxDisplayItems={2}
                                    className="flex min-w-[295px] items-center justify-center gap-2 bg-fuschia-700 px-[12px] text-base text-white">
                                    All topics
                                </CategoryButton>
                            </div>

                            {showContributorFilter && (
                                <div className="z-1 relative flex flex-col">
                                    <FilterLabel>Authors</FilterLabel>
                                    <CategoryButton
                                        multiselect={true}
                                        enableSmartSearch={true}
                                        searchPlaceholder="Search contributors..."
                                        options={contributors.map((contributor) => ({
                                            value: contributor.identifier || contributor.inode,
                                            label: `${contributor.firstName} ${contributor.lastName}`
                                        }))}
                                        selectedOptions={mapContributorsToOptions(
                                            activeContributors,
                                            contributors
                                        )}
                                        onSelectionChange={(selectedContributors) =>
                                            handleFilterChange('contributors', selectedContributors)
                                        }
                                        defaultText="All contributors"
                                        maxDisplayItems={2}
                                        className="flex min-w-[295px] items-center justify-center gap-2 bg-fuschia-700 px-[12px] text-base text-white">
                                        All contributors
                                    </CategoryButton>
                                </div>
                            )}

                            {isSearchByTags &&
                                showContributorFilter &&
                                (activeTags.length > 0 || activeContributors.length > 0) && (
                                    <div className="mt-3 flex items-end md:mt-0">
                                        <CategoryButton
                                            className="z-1 relative w-full max-w-full !py-[9px] px-2 text-base hover:!bg-fuschia-200 hover:text-black sm:w-full md:max-w-[225px] md:px-10"
                                            onClick={handleClearAllFilters}>
                                            Clear All
                                        </CategoryButton>
                                    </div>
                                )}

                            {isSearchByTags && (
                                <div className="ml-auto hidden items-end md:flex">
                                    <div className="flex overflow-hidden rounded-lg border border-gray-300">
                                        <button
                                            type="button"
                                            onClick={() => setIsShowInList(false)}
                                            className={`flex items-center justify-center p-2 transition-colors ${
                                                !isShowInList
                                                    ? 'bg-fuschia-700 text-white'
                                                    : 'bg-white text-gray-600 hover:bg-gray-100'
                                            }`}
                                            aria-label="Grid view"
                                            aria-pressed={!isShowInList}>
                                            <IconLayoutGrid className="size-5" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setIsShowInList(true)}
                                            className={`flex items-center justify-center p-2 transition-colors ${
                                                isShowInList
                                                    ? 'bg-fuschia-700 text-white'
                                                    : 'bg-white text-gray-600 hover:bg-gray-100'
                                            }`}
                                            aria-label="List view"
                                            aria-pressed={isShowInList}>
                                            <IconList className="size-5" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <ListingContentlets
                        Card={CardComponent}
                        showTopMargin={showTopMargin}
                        maxLimit={maxLimit}
                        showAuthor={!!showAuthor}
                        contentlets={actualPosts}
                        centerBlogs={!!centerBlogs}
                        showInSingleRow={true}
                        isAllPostsSection={isAllPostsSection}
                        isShowInList={isShowInList}
                    />

                    {canLoadMore && (
                        <div className="mt-6 text-center">
                            <CategoryButton
                                className="!py-2 px-10 text-base hover:!bg-fuschia-200 hover:text-black"
                                onClick={handleLoadMore}>
                                Load more
                            </CategoryButton>
                        </div>
                    )}

                    {(isSearchByTags || showContributorFilter) && loading && (
                        <LoadingPosts className="mt-12" />
                    )}

                    {(isSearchByTags || showContributorFilter) &&
                        !loading &&
                        (activeTags.length > 0 || activeContributors.length > 0) &&
                        actualPosts.length === 0 && <NoResults className="mt-12" as="p" />}
                </>
            )}
        </div>
    );
}

export default RelatedBlogs;
